import { useCallback, useEffect, useRef, useState } from "react";
import { studioRequest } from "./api";

// Studio jobs being made, by key (a style, a part): each { status, position, results, error },
// checked every few seconds until it's done or failed.
export default function useStudioJobs() {
  const [jobs, setJobs] = useState({}), live = useRef({}), alive = useRef(true);
  useEffect(() => () => { alive.current = false; Object.values(live.current).forEach(clearTimeout); }, []);
  const watch = useCallback((key, job) => {
    clearTimeout(live.current[key]);
    setJobs((all) => ({ ...all, [key]: job }));
    if (job.status === "done" || job.status === "failed") return;
    live.current[key] = setTimeout(async () => {
      if (!alive.current) return;
      try { const { job: next } = await studioRequest(`jobs/${job.id}`); if (alive.current) watch(key, next); }
      catch (failure) { if (alive.current) setJobs((all) => ({ ...all, [key]: { ...job, status: "failed", error: failure.message } })); }
    }, 2500);
  }, []);
  const fail = useCallback((key, message) => setJobs((all) => ({ ...all, [key]: { status: "failed", error: message, results: [] } })), []);
  const reset = useCallback(() => { Object.values(live.current).forEach(clearTimeout); live.current = {}; setJobs({}); }, []);
  return { jobs, watch, fail, reset };
}

// What to say while a job is on its way.
export function progressOf(job) {
  if (!job) return "";
  // `warming`: no GPU was running, so one is starting (to split a song into its parts).
  // `note`: what the composer is doing now ("Making it with ElevenLabs…").
  if (job.note && (job.status === "queued" || job.status === "running")) return job.note;
  if (job.status === "queued") return job.warming ? "Starting the studio…" : job.position > 1 ? `Waiting (${job.position - 1} ahead)…` : "Starting…";
  if (job.status === "running") return "Making it…";
  if (job.status === "failed") return job.error || "That didn't work.";
  return "";
}
